import React, { useState } from 'react';
import { PlayIcon } from '@heroicons/react/24/solid';
import { toEmbedUrl, getVideoThumbnail, youTubeThumbnailFallback } from '../../utils/videoEmbed';

interface VideoEmbedProps {
  url: string;
  title?: string;
}

/**
 * PIPEDA-conscious video embed (Phase 3d). Click-to-load facade: the actual
 * player iframe (cookies/tracking JS) only loads when the visitor presses play.
 *
 * The facade shows the real YouTube poster thumbnail when available — note this
 * is a third-party IMAGE request to img.youtube.com on load (leaks IP/referrer,
 * but no tracking JS or cookies, unlike the full player). Acceptable for a
 * public, non-PHI directory page. Vimeo has no static thumbnail, so it keeps the
 * branded gradient.
 */
const VideoEmbed: React.FC<VideoEmbedProps> = ({ url, title = 'Intro video' }) => {
  const [loaded, setLoaded] = useState(false);
  const [thumb, setThumb] = useState<string | null>(getVideoThumbnail(url));
  const embedUrl = toEmbedUrl(url);
  if (!embedUrl) return null;

  return (
    <div className="relative w-full overflow-hidden rounded-card bg-clinic-dark aspect-video">
      {loaded ? (
        <iframe
          src={`${embedUrl}${embedUrl.includes('?') ? '&' : '?'}autoplay=1`}
          title={title}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <button
          type="button"
          onClick={() => setLoaded(true)}
          className="group absolute inset-0 w-full h-full flex flex-col items-center justify-center transition-colors"
          aria-label="Play intro video"
        >
          {/* Poster: real thumbnail if available, else branded gradient */}
          {thumb ? (
            <>
              <img
                src={thumb}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover"
                // maxresdefault may not exist for every video → fall back to hqdefault once.
                onError={() => setThumb(youTubeThumbnailFallback(url))}
              />
              <div className="absolute inset-0 bg-clinic-dark/40 group-hover:bg-clinic-dark/30 transition-colors" />
            </>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-clinic-primary to-clinic-primary-dark" />
          )}

          <span className="relative flex items-center justify-center h-16 w-16 rounded-full bg-white/90 group-hover:bg-white shadow-lg transition-transform group-hover:scale-105">
            <PlayIcon className="h-8 w-8 text-clinic-primary ml-1" />
          </span>
          <span className="relative mt-3 text-sm text-white [text-shadow:0_1px_4px_rgba(0,0,0,0.5)]">Watch intro video</span>
        </button>
      )}
    </div>
  );
};

export default VideoEmbed;
